import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Lazy loading de páginas principales
import { Suspense, lazy } from 'react';
import { LoadingScreen } from '@components/common/LoadingScreen';

// Landing page (Pública)
const LandingPage = lazy(() => import('@features/landing/pages/LandingPage'));

// Auth pages
const LoginPage = lazy(() => import('@features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@features/auth/pages/RegisterPage'));
const PasswordResetPage = lazy(() => import('@features/auth/pages/PasswordResetPage'));
const GoogleCallbackPage = lazy(() => import('@features/auth/pages/GoogleCallbackPage'));

// Admin pages (Fase 1)
const DashboardPage = lazy(() => import('@features/admin/pages/DashboardPage'));
const ClientsPage = lazy(() => import('@features/admin/pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('@features/admin/pages/ClientDetailPage'));
const ClientEditPage = lazy(() => import('@features/admin/pages/ClientEditPage'));
const ServicesPage = lazy(() => import('@features/admin/pages/ServicesPage'));
const CitasPage = lazy(() => import('@features/admin/pages/CitasPage'));
const TechniciansPage = lazy(() => import('@features/admin/pages/TechniciansPage'));
const ReportsPage = lazy(() => import('@features/admin/pages/ReportsPage'));
const OperationalReportsPage = lazy(() => import('@features/admin/pages/OperationalReportsPage'));
const PropertiesMapPage = lazy(() => import('@features/admin/pages/PropertiesMapPage'));
const OperationsMapPage = lazy(() =>
  import('@features/admin/pages/OperationsMapPage').then((m) => ({ default: m.OperationsMapPage }))
);
const RouteOptimizationPage = lazy(() =>
  import('@features/admin/pages/RouteOptimizationPage').then((m) => ({ default: m.RouteOptimizationPage }))
);
const SettingsPage = lazy(() => import('@features/admin/pages/SettingsPage'));
const PipelinePage = lazy(() => import('@features/admin/pages/PipelinePage'));
const LeadsPage = lazy(() => import('@features/admin/pages/LeadsPage'));
const InventoryPage = lazy(() => import('@features/admin/pages/InventoryPage'));
const NotificationsPage = lazy(() =>
  import('@features/shared/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
);

// Public pages
const OfflinePage = lazy(() =>
  import('@features/admin/pages/OfflinePage').then((m) => ({ default: m.default }))
);

// Client Portal pages (Fase 1)
const ClientDashboardPage = lazy(
  () => import('@features/client-portal/pages/ClientDashboardPage')
);
const ServiceHistoryPage = lazy(
  () => import('@features/client-portal/pages/ServiceHistoryPage')
);
const ClientProfilePage = lazy(
  () => import('@features/client-portal/pages/ClientProfilePage')
);
const PortalCitaDetailPage = lazy(
  () => import('@features/client-portal/pages/PortalCitaDetailPage')
);
const ServiceHistoryReportPage = lazy(
  () => import('@features/client-portal/pages/ServiceHistoryReportPage').then((m) => ({
    default: m.ServiceHistoryReportPage,
  }))
);

// Technician pages (Fase 2 - PWA)
const TechnicianDashboardPage = lazy(
  () => import('@features/technician/pages/TechnicianDashboardPage')
);
const CitasListPage = lazy(
  () => import('@features/technician/pages/CitasListPage')
);
const CitaDetailPage = lazy(
  () => import('@features/technician/pages/CitaDetailPage')
);
const HistoryPage = lazy(
  () => import('@features/technician/pages/HistoryPage')
);
const ProfilePage = lazy(
  () => import('@features/technician/pages/ProfilePage')
);
const MyPerformancePage = lazy(
  () => import('@features/technician/pages/MyPerformancePage')
);
const DailyKitPage = lazy(
  () => import('@features/technician/pages/DailyKitPage')
);

// Layouts
import { AdminLayout } from '@components/layout/AdminLayout';
import { ClientLayout } from '@components/layout/ClientLayout';
import { AuthLayout } from '@components/layout/AuthLayout';
import TechnicianLayout from '@components/layout/TechnicianLayout';
import { ProtectedRoute } from '@components/common/ProtectedRoute';
import { AuthProvider } from '@features/auth/context/AuthContext';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Landing page pública */}
            <Route path="/" element={<LandingPage />} />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/password-reset" element={<PasswordResetPage />} />
            </Route>

            {/* Google OAuth Callback - Protected */}
            <Route
              path="/auth/google/callback"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                  <GoogleCallbackPage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes - Protected */}
            <Route
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'admin', 'supervisor_tecnico']}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<DashboardPage />} />
              <Route path="/admin/clients" element={<ClientsPage />} />
              <Route path="/admin/clients/:id" element={<ClientDetailPage />} />
              <Route path="/admin/clients/edit/:id" element={<ClientEditPage />} />
              <Route path="/admin/servicios" element={<ServicesPage />} />
              <Route path="/admin/citas" element={<CitasPage />} />
              <Route path="/admin/tecnicos" element={<TechniciansPage />} />
              <Route path="/admin/reportes" element={<ReportsPage />} />
              <Route path="/admin/reportes/operativos" element={<OperationalReportsPage />} />
              <Route path="/admin/mapa" element={<Navigate to="/admin/mapa/propiedades" replace />} />
              <Route path="/admin/mapa/propiedades" element={<PropertiesMapPage />} />
              <Route path="/admin/mapa/operaciones" element={<OperationsMapPage />} />
              <Route path="/admin/rutas" element={<RouteOptimizationPage />} />
              <Route path="/admin/pipeline" element={<PipelinePage />} />
              <Route path="/admin/leads" element={<LeadsPage />} />
              <Route path="/admin/inventario" element={<InventoryPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/notificaciones" element={<NotificationsPage />} />
            </Route>

            {/* Client Portal routes - Protected */}
            <Route
              element={
                <ProtectedRoute allowedRoles={['cliente']}>
                  <ClientLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="/portal/dashboard" element={<ClientDashboardPage />} />
              <Route path="/portal/historial" element={<ServiceHistoryPage />} />
              <Route path="/portal/historial/reporte" element={<ServiceHistoryReportPage />} />
              <Route path="/portal/citas/:id" element={<PortalCitaDetailPage />} />
              <Route path="/portal/perfil" element={<ClientProfilePage />} />
              <Route path="/portal/notificaciones" element={<NotificationsPage />} />
            </Route>

            {/* Technician App routes - Protected (Fase 2 - PWA) */}
            <Route
              element={
                <ProtectedRoute allowedRoles={['tecnico']}>
                  <TechnicianLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/technician" element={<TechnicianDashboardPage />} />
              <Route path="/technician/citas" element={<CitasListPage />} />
              <Route path="/technician/citas/:id" element={<CitaDetailPage />} />
              <Route path="/technician/historial" element={<HistoryPage />} />
              <Route path="/technician/perfil" element={<ProfilePage />} />
              <Route path="/technician/performance" element={<MyPerformancePage />} />
              <Route path="/technician/kit" element={<DailyKitPage />} />
              <Route path="/technician/notificaciones" element={<NotificationsPage />} />
            </Route>

            {/* Public utility routes */}
            <Route path="/offline" element={<OfflinePage />} />

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </AuthProvider>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#2d3838',
            color: '#fff',
            borderRadius: '8px',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

